// 'extends object' must be used instead of Record<string, unknown> because react-table uses object internally
/* eslint-disable @typescript-eslint/ban-types */
import { useLayoutEffect } from 'react';

import {
  actions,
  ActionType,
  Hooks,
  IdType,
  TableInstance,
  TableState,
} from 'react-table';

import { useLocalState } from '@adsk/alloy-react-helpers';

import { ColumnState, ColumnStateSetter } from '../types';

const pluginName = 'useStoredColumnState';

// Actions
actions.columnRestoreSize = 'columnRestoreSize';

export interface UseStoredColumnState {
  storageKey?: string;
}

export type UseStoredColumnStateProps<D extends object = {}> = {
  storageKey?: string;
  setColumnState?: ColumnStateSetter<D>;
};

const reducer = <D extends object = {}>(
  state: TableState<D>,
  action: ActionType,
  previousState?: TableState<D>,
  instance?: TableInstance<D>
) => {
  if (!instance) {
    return state;
  }

  const { storageKey, setColumnState, visibleColumns } = instance;

  if (action.type === actions.columnRestoreSize) {
    if (!storageKey) {
      return state;
    }

    const rawColumnState = window.localStorage.getItem(storageKey);

    if (!rawColumnState) {
      return state;
    }

    const { columnWidths }: ColumnState<D> = JSON.parse(rawColumnState);

    if (!columnWidths) {
      return state;
    }

    Object.keys(columnWidths).forEach((column) => {
      const columnInstance = visibleColumns.find((c) => c.id === column);

      if (columnInstance) {
        columnInstance.width = columnWidths[column].current;
      }
    });
  }

  if (action.type === actions.columnDoneResizing) {
    const columnNames = Object.keys(state.columnResizing.columnWidths);
    const columnWidthsArray = columnNames.map((col) => {
      return {
        original: state.columnResizing.columnWidth,
        current: state.columnResizing.columnWidths[col],
      };
    });

    const columnWidths = columnNames.reduce((obj, current, i) => {
      return {
        ...obj,
        [current]: columnWidthsArray[i],
      };
    }, {} as Record<IdType<D>, { original: number; current: number }>);

    setColumnState?.((prevState) => {
      return {
        ...prevState,
        columnWidths,
      };
    });
  }

  return state;
};

const useInstance = <D extends object = {}>(instance: TableInstance<D>) => {
  const {
    storageKey,
    setColumnOrder,
    setTableSizeTheme,
    allColumns,
    dispatch,
  } = instance;

  const [columnState, setColumnState] =
    useLocalState<ColumnState<D>>(storageKey);

  useLayoutEffect(() => {
    if (columnState?.order) {
      setColumnOrder(columnState.order);
    }
  }, [columnState?.order, setColumnOrder]);

  useLayoutEffect(() => {
    if (columnState?.columnWidths) {
      dispatch({ type: actions.columnRestoreSize });
    }
  }, [dispatch, columnState?.columnWidths]);

  useLayoutEffect(() => {
    if (columnState?.size) {
      setTableSizeTheme(columnState.size);
    }
  }, [columnState?.size, setTableSizeTheme]);

  useLayoutEffect(() => {
    if (columnState?.hiddenColumnIds) {
      allColumns.forEach((column) => {
        if (
          columnState?.hiddenColumnIds &&
          columnState.hiddenColumnIds[column.id] === true &&
          column.isVisible
        ) {
          column.toggleHidden();
        }
      });
    }
  }, [columnState?.hiddenColumnIds, allColumns]);

  const additions: UseStoredColumnStateProps<D> = {
    setColumnState,
  };

  Object.assign(instance, additions);
};

export const useStoredColumnState = <D extends object = {}>(
  hooks: Hooks<D>
) => {
  hooks.stateReducers.push(reducer);
  hooks.useInstance.push(useInstance);
};

useStoredColumnState.pluginName = pluginName;
